import React, { useState, useEffect } from 'react'
import { X, BookOpen, Lock } from 'lucide-react'
import playCreateStorageService from '../services/playCreateStorageService'

export const DiaryViewer = ({ character, favorability, onClose }) => {
  const [diaries, setDiaries] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedDiary, setSelectedDiary] = useState(null)

  useEffect(() => {
    const loadDiaries = async () => {
      try {
        setIsLoading(true)
        const loadedDiaries = await playCreateStorageService.getCharacterDiaries(
          character.id,
          favorability
        )
        setDiaries(loadedDiaries)
      } catch (error) {
        console.error('❌ 加载日记失败:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadDiaries()
  }, [character.id, favorability])

  const formatDate = (timestamp) => {
    const date = new Date(timestamp)
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 rounded-lg w-full max-w-3xl max-h-[80vh] flex flex-col border border-dark-600">
        {/* 头部 */}
        <div className="flex items-center justify-between p-6 border-b border-dark-600">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <BookOpen size={24} className="text-primary" />
            {character.name}的日记
          </h2>
          <button
            onClick={onClose}
            className="text-dark-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* 内容区域 */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="text-center text-dark-400 py-20">
              <BookOpen size={48} className="mx-auto mb-4 animate-pulse text-amber-500/50" />
              <p>加载日记中...</p>
            </div>
          ) : diaries.length === 0 ? (
            <div className="text-center text-dark-400 py-20">
              <BookOpen size={48} className="mx-auto mb-4 text-dark-600" />
              <p className="text-lg mb-2">还没有日记记录</p>
              <p className="text-sm text-dark-500">
                {character.name}会在每次对话后写下日记<br />
                继续聊天来解锁更多内容吧！
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-sm text-dark-400 mb-4 pb-3 border-b border-dark-600">
                共有 {diaries.length} 篇日记 • {diaries.filter(d => d.unlocked).length} 篇已解锁
              </div>
              {diaries.map((diary) => (
                <div
                  key={diary.id}
                  className={`group relative bg-gradient-to-br from-dark-700 to-dark-700/50 rounded-xl p-4 border transition-all ${
                    diary.unlocked
                      ? 'border-amber-500/30 hover:border-amber-500/50 hover:shadow-lg hover:shadow-amber-500/10 cursor-pointer'
                      : 'border-dark-600 opacity-60'
                  }`}
                  onClick={() => diary.unlocked && setSelectedDiary(diary)}
                >
                  {/* 日期和状态 */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {!diary.unlocked ? (
                        <Lock size={16} className="text-dark-400" />
                      ) : (
                        <BookOpen size={16} className="text-amber-400" />
                      )}
                      <span className="text-sm font-medium text-dark-300">
                        {formatDate(diary.date)}
                      </span>
                    </div>
                    {!diary.unlocked && (
                      <span className="text-xs bg-dark-600 text-dark-400 px-2 py-1 rounded-full">
                        🔒 需要好感度 {diary.favorabilityRequirement}
                      </span>
                    )}
                  </div>
                  
                  {/* 日记内容预览 */}
                  {diary.unlocked ? (
                    <>
                      <p className="text-sm text-white/90 leading-relaxed line-clamp-2 mb-2">
                        {diary.content}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-dark-400">点击查看完整内容</span>
                        {diary.mood && (
                          <span className="text-xs">
                            {diary.mood === 'neutral' ? '😐' :
                             diary.mood === 'friendly' ? '😊' :
                             diary.mood === 'happy' ? '😄' :
                             diary.mood === 'loving' ? '💕' : '✨'}
                          </span>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center gap-2 text-dark-500 italic text-sm">
                      <Lock size={14} />
                      <p>这篇日记被锁住了，提升好感度后才能查看...</p>
                    </div>
                  )}
                  
                  {/* 悬停效果 */}
                  {diary.unlocked && (
                    <div className="absolute inset-0 bg-amber-500/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 日记详情模态框 */}
        {selectedDiary && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-60 p-4">
            <div className="bg-gradient-to-br from-dark-800 to-dark-900 rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col border-2 border-amber-500/30 shadow-2xl shadow-amber-500/20">
              {/* 头部 - 复古日记本样式 */}
              <div className="relative p-6 border-b border-amber-500/20">
                <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-amber-500/10 to-transparent" />
                <div className="relative flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <BookOpen size={20} className="text-amber-400" />
                      <h3 className="text-xl font-bold text-amber-100">
                        {character.name}的日记
                      </h3>
                    </div>
                    <p className="text-sm text-dark-400">
                      {formatDate(selectedDiary.date)}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedDiary(null)}
                    className="p-2 hover:bg-dark-700 rounded-full transition-colors text-dark-400 hover:text-white"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>
              
              {/* 日记内容 - 手写风格 */}
              <div className="flex-1 overflow-y-auto p-8">
                <div className="bg-amber-50/5 rounded-lg p-6 border border-amber-500/10">
                  <div className="text-amber-100/90 whitespace-pre-wrap leading-loose text-base font-light tracking-wide">
                    {selectedDiary.content}
                  </div>
                  
                  {/* 日记落款 */}
                  <div className="mt-6 pt-4 border-t border-amber-500/10 flex justify-end items-center gap-2">
                    <span className="text-sm text-dark-400 italic">
                      —— {character.name}
                    </span>
                    {selectedDiary.mood && (
                      <span className="text-lg">
                        {selectedDiary.mood === 'neutral' ? '😐' :
                         selectedDiary.mood === 'friendly' ? '😊' :
                         selectedDiary.mood === 'happy' ? '😄' :
                         selectedDiary.mood === 'loving' ? '💕' : '✨'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* 底部提示 */}
              <div className="p-4 border-t border-dark-600 bg-dark-800/50">
                <p className="text-xs text-center text-dark-400">
                  💡 继续对话可以解锁更多日记内容
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

